import React, { useMemo, useRef, useState } from "react";
import { Button, Card, Container, Modal } from "react-bootstrap";
import { FileText, Save } from "react-feather";
import { Helmet } from "react-helmet-async";
import CommonTable from "../../components/tables/CommonTable";
import { NavLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import api from "../../api";
import { downloadFile } from "../../utils/staticMethods";

const ExportButton = (props) => {
  const { title, onClick } = props;
  const [show, setShow] = useState(false);

  return (
    <>
      <Button
        variant="primary"
        onClick={(e) => {
          setShow(true);
        }}
        className="mx-1 py-0"
      >
        <div className="d-flex justify-content-between align-items-center">
          <Save size={"14"} />
          <div className="d-none d-xl-inline">&nbsp;{title}</div>
        </div>
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will create a CSV export file for download. This will be done in
          the background, and may take some time, depending on the size of the
          export. The export will appear in your browser downloads when
          complete.
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={(e) => {
              setShow(false);
              onClick(e);
            }}
            variant={"primary"}
          >
            {title}
          </Button>
          <Button onClick={() => setShow(false)} variant={"secondary"}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const Reports = ({ reportType, children, ...rest }) => {
  const [resultCount, setResultCount] = useState(0);
  const [showDelete, setShowDelete] = useState(false);
  const [actionId, setActionId] = useState();
  const childRef = useRef();
  const { user } = useAuth();

  const handleExport = async (report) => {
    try {
      var sortColumns = report.columns
        .filter((_) => _.isSorted === true)
        .map((_) => {
          return { id: _.columnId, desc: _.isSortedDesc };
        });
      var filterColumns = report.columns
        .filter((_) => _.filterValue !== null)
        .map((_) => {
          return { id: _.columnId, value: _.filterValue };
        });
      var data = {
        pageIndex: 1,
        pageSize: report.pageSize,
        sortBy: sortColumns,
        filters: filterColumns,
      };
      var blob = await api.post("assets/searchexport", data);
      downloadFile(blob);
    } catch (error) {
    } finally {
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "name",
        // type: "link",
        // link: "/rules/edit",
        // stateId: "id",
        Cell: (cell) => (
          <NavLink
            className="m-0 p-0"
            to={{
              pathname: `/${cell.row.original.type}${
                cell.row.original.type !== "users" ? "/search" : ""
              }`,
              search: `?reportid=${cell.row.original.id}`,
            }}
          >
            {cell.row.original.name}
          </NavLink>
        ),
      },
      {
        Header: "Type",
        accessor: "type",
        // type: "rule",
      },
      {
        Header: "Category",
        Cell: (cell) => (
          <div className="m-0 p-0">
            {cell.row.original.allUsers
              ? `${user.customerName} Report`
              : "Personal Report"}
          </div>
        ),
        disableSortBy: true,
      },
      {
        Header: "Actions",
        Cell: (cell) => (
          <div className="d-flex m-0 p-0">
            <div className="p-0 m-0">
              <ExportButton
                title={"Export"}
                onClick={(e) => {
                  handleExport(cell.row.original);
                }}
              />
            </div>
            <div className="p-0 m-0">
              <NavLink
                className="m-0 p-0"
                to={{
                  pathname: `/${cell.row.original.type}${
                    cell.row.original.type !== "users" ? "/search" : ""
                  }`,
                  search: `?reportid=${cell.row.original.id}`,
                }}
              >
                <Button className="mx-1 py-0" variant="primary">
                  View
                </Button>
              </NavLink>
            </div>
            {user?.showMap && cell.row.original.type === "assets" && (
              <NavLink
                className="m-0 p-0"
                to={{
                  // pathname: `/report/${cell.row.original.id}/map`,
                  pathname: `/maps?reportid=${cell.row.original.id}`,
                }}
              >
                <div className="p-0 m-0">
                  <Button className="mx-1 py-0" variant="primary">
                    View on map
                  </Button>
                </div>
              </NavLink>
            )}
            {cell.row.original.ownerId === user.id && (
              <div className="p-0 m-0">
                <Button
                  className="ms-3 py-0"
                  variant="danger"
                  onClick={(e) => {
                    setActionId(cell.row.original.id);
                    setShowDelete(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
        ),
        disableSortBy: true,
        width: 100,
      },
    ];
  }, [user]);

  const handleDelete = async () => {
    try {
      api.delete("reports/" + actionId);
      childRef.current.updateTable(1);
    } catch (error) {
      childRef.current.updateTable(1);
    } finally {
      setActionId(undefined);
      setShowDelete(false);
    }
  };

  return (
    <React.Fragment>
      <Helmet defer={false} title="Reports" />
      <Container fluid className="p-0">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="h3 mb-0">
            <FileText className="me-2" />
            {reportType} Reports{" "}
            {typeof resultCount === "string" && `(${resultCount})`}
          </h1>
          {/* {(user?.role === "Admin" || user?.createRules) && (
            <NavLink to="/rules/create">
              <Button className="text-nowrap">
                <FontAwesomeIcon icon={faPlus} fixedWidth className="me-1" />
                Create New
              </Button>
            </NavLink>
          )} */}
        </div>
        <Card className="bg-white p-3">
          <CommonTable
            ref={childRef}
            setCountParent={setResultCount}
            apiName="reports"
            columns={columns}
          />
        </Card>
      </Container>
      <Modal
        show={showDelete}
        onHide={() => {
          setActionId(undefined);
          setShowDelete(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Please Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure that you wish to permanently delete this Report?</p>
          <p>Deletions are not reversible.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => handleDelete()}>
            Delete
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setActionId(undefined);
              setShowDelete(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Reports;
